/**
 * UGC Carousel — Swiper-powered
 *
 * Swiper's bundle is loaded globally from theme.liquid. It owns the loop /
 * centered-slide / navigation / autoplay mechanics. This file only layers
 * UGC-specific behaviour on top:
 *   - guarantees there are enough slides in the DOM for loop mode to build
 *     its duplicates (Swiper silently disables loop otherwise)
 *   - sets up the mobile layout: one full card plus a half-peek of the next,
 *     left-aligned; tablet and desktop stay centred
 *   - plays the active slide's video, pauses every other one
 *   - drives that video's progress bar off its own timeupdate event
 *   - advances the carousel when that video ends, and starts the next
 *     video's progress bar from zero the moment it becomes active
 *   - hands control back to Swiper's autoplay when the active slide has
 *     no video, so the two systems never fight over the same transition
 *   - renders its own pagination dots (one per *original* slide, not per
 *     duplicate) and keeps them in sync with realIndex
 *
 * Card scaling is handled entirely in CSS by animating height, so there is
 * no gap/spacing maths here: the card's width never changes, which means
 * `spaceBetween` is always exactly the merchant's gap setting.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function waitForSwiper(callback, attempts) {
    attempts = attempts || 0;
    if (typeof Swiper !== 'undefined') { callback(); return; }
    if (attempts > 50) return; // ~5s, then give up quietly rather than loop forever
    setTimeout(function () { waitForSwiper(callback, attempts + 1); }, 100);
  }

  function initAll() {
    var roots = document.querySelectorAll('.ugc-carousel[data-loop]:not([data-swiper-initialized])');
    Array.prototype.forEach.call(roots, initOne);
  }

  function resetProgress(progressFill) {
    if (!progressFill) return;
    progressFill.style.transition = 'none';
    progressFill.style.width = '0%';
    void progressFill.offsetWidth; 
    progressFill.style.transition = '';
  }

  function initOne(root) {
    if (root.dataset.swiperInitialized === 'true') return;
    root.dataset.swiperInitialized = 'true';

    var wrapper = root.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    var originalSlides = Array.prototype.slice.call(wrapper.querySelectorAll('.ugc-carousel__slide'));
    var originalCount = originalSlides.length;
    if (!originalCount) return;

    var autoplayEnabled = root.dataset.autoplay === 'true';
    var autoplaySpeed = parseInt(root.dataset.autoplaySpeed, 10) || 4000;
    var videoAutoplayOnActive = root.dataset.videoAutoplay !== 'false';
    var transitionDuration = parseInt(root.dataset.transitionDuration, 10) || 500;
    var cardGap = parseInt(root.dataset.cardGap, 10) || 16;
    var perViewMobile = parseFloat(root.dataset.cardsPerViewMobile) || 1.5;
    var perViewTablet = parseFloat(root.dataset.cardsPerViewTablet) || 3;
    var perViewDesktop = parseFloat(root.dataset.cardsPerViewDesktop) || 5;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var loop = root.dataset.loop === 'true' && originalCount > 1;
    if (loop) {
      var required = (Math.ceil(Math.max(perViewMobile, perViewTablet, perViewDesktop)) * 2) + 1;
      var passes = 0;
      while (wrapper.children.length < required && passes < 12) {
        passes++;
        originalSlides.forEach(function (slide) {
          var clone = slide.cloneNode(true);
          clone.setAttribute('data-ugc-duplicate', 'true');
          clone.removeAttribute('data-shopify-editor-block');
          clone.removeAttribute('id');
          wrapper.appendChild(clone);
        });
      }
    }

    var prevEl = root.querySelector('.ugc-carousel__arrow--prev');
    var nextEl = root.querySelector('.ugc-carousel__arrow--next');

    var dotsEl = root.querySelector('.ugc-carousel__dots');
    var dots = [];
    if (dotsEl) {
      dotsEl.innerHTML = '';
      for (var i = 0; i < originalCount; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ugc-carousel__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        dot.dataset.index = String(i);
        dotsEl.appendChild(dot);
        dots.push(dot);
      }
    }

    var swiper = new Swiper(root, {
      loop: loop,
      centeredSlides: false,
      slidesPerView: perViewMobile,
      breakpoints: {
        750: { slidesPerView: perViewTablet, centeredSlides: true },
        990: { slidesPerView: perViewDesktop, centeredSlides: true }
      },
      spaceBetween: cardGap,
      slidesPerGroup: 1,
      watchSlidesProgress: true,
      grabCursor: true,
      simulateTouch: true,
      allowTouchMove: true,
      speed: reducedMotion ? 0 : transitionDuration,
      keyboard: { enabled: true, onlyInViewport: true },
      navigation: (prevEl && nextEl) ? { prevEl: prevEl, nextEl: nextEl } : false,

      autoplay: (autoplayEnabled && !reducedMotion) ? {
        delay: autoplaySpeed,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      } : false,
      on: {
        init: function () { syncActiveSlide(this); },
        slideChange: function () { syncActiveSlide(this); }
      }
    });

    dots.forEach(function (dotEl) {
      dotEl.addEventListener('click', function () {
        var index = parseInt(dotEl.dataset.index, 10) || 0;
        if (loop && typeof swiper.slideToLoop === 'function') swiper.slideToLoop(index);
        else swiper.slideTo(index);
      });
    });

    function syncDots(swiperInstance) {
      if (!dots.length) return;
      var real = (loop ? swiperInstance.realIndex : swiperInstance.activeIndex) % originalCount;
      dots.forEach(function (dotEl, index) {
        var isActive = index === real;
        dotEl.classList.toggle('is-active', isActive);
        dotEl.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    function syncActiveSlide(swiperInstance) {
      var slides = swiperInstance.slides;
      var activeSlideEl = slides[swiperInstance.activeIndex];

      for (var i = 0; i < slides.length; i++) {
        handleVideoForSlide(slides[i], slides[i] === activeSlideEl, swiperInstance);
      }

      syncDots(swiperInstance);

      if (!swiperInstance.autoplay) return;

      var activeHasVideo = !!(activeSlideEl && activeSlideEl.querySelector('video') && videoAutoplayOnActive);

      if (activeHasVideo) {
        if (swiperInstance.autoplay.running) swiperInstance.autoplay.stop();
      } else if (autoplayEnabled && !reducedMotion) {
        if (!swiperInstance.autoplay.running) swiperInstance.autoplay.start();
      }
    }

    function handleVideoForSlide(slideEl, isActive, swiperInstance) {
      var video = slideEl.querySelector('video');
      if (!video) return;
      var progressFill = slideEl.querySelector('.ugc-card__progress-fill');
      var playBtn = slideEl.querySelector('.ugc-card__control--play');

      if (video._ugcTimeUpdateHandler) {
        video.removeEventListener('timeupdate', video._ugcTimeUpdateHandler);
        video.removeEventListener('ended', video._ugcEndedHandler);
        video._ugcTimeUpdateHandler = null;
        video._ugcEndedHandler = null;
      }

      if (isActive && videoAutoplayOnActive) {

        video.loop = false;

        resetProgress(progressFill);

        try { video.currentTime = 0; } catch (e) { /* metadata not loaded yet — ignore */ }
        video.play().catch(function () {});

        if (playBtn) {
          playBtn.classList.add('is-playing');
          playBtn.setAttribute('aria-pressed', 'true');
        }

        if (progressFill) {
          var onTimeUpdate = function () {
            if (!video.duration || !isFinite(video.duration)) return;
            progressFill.style.width = ((video.currentTime / video.duration) * 100) + '%';
          };
          var onEnded = function () {
            progressFill.style.width = '100%';
            swiperInstance.slideNext();
          };

          video.addEventListener('timeupdate', onTimeUpdate);
          video.addEventListener('ended', onEnded);
          video._ugcTimeUpdateHandler = onTimeUpdate;
          video._ugcEndedHandler = onEnded;
        }
      } else {
        video.pause();
        resetProgress(progressFill);
        if (playBtn) {
          playBtn.classList.remove('is-playing');
          playBtn.setAttribute('aria-pressed', 'false');
        }
      }
    }
  }


  function bindGlobalControls() {
    if (window.__ugcCarouselControlsBound) return;
    window.__ugcCarouselControlsBound = true;

    document.addEventListener('click', function (event) {
      var playBtn = event.target.closest('.ugc-card__control--play');
      var muteBtn = event.target.closest('.ugc-card__control--mute');
      if (playBtn) togglePlay(playBtn);
      if (muteBtn) toggleMute(muteBtn);
    });

    document.addEventListener('click', function (event) {
      var button = event.target.closest('.ugc-product-card__cta');
      if (!button) return;
      event.preventDefault();
      if (button.disabled) return;

      var variantId = button.dataset.variantId;
      var originalHTML = button.innerHTML;
      button.disabled = true;

      fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          if (data.status) throw new Error(data.description || 'Unable to add to cart');
          button.innerHTML = '✓';
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: data, bubbles: true }));
          setTimeout(function () { button.innerHTML = originalHTML; button.disabled = false; }, 1500);
        })
        .catch(function () {
          button.innerHTML = '!';
          setTimeout(function () { button.innerHTML = originalHTML; button.disabled = false; }, 1500);
        });
    });
  }

  function togglePlay(button) {
    var video = button.closest('.ugc-card__video-wrap').querySelector('video');
    if (!video) return;
    if (video.paused) {
      video.play();
      button.classList.add('is-playing');
      button.setAttribute('aria-pressed', 'true');
    } else {
      video.pause();
      button.classList.remove('is-playing');
      button.setAttribute('aria-pressed', 'false');
    }
  }

  function toggleMute(button) {
    var video = button.closest('.ugc-card__video-wrap').querySelector('video');
    if (!video) return;
    video.muted = !video.muted;
    button.classList.toggle('is-muted', video.muted);
    button.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
  }

  bindGlobalControls();
  ready(function () { waitForSwiper(initAll); });

  document.addEventListener('shopify:section:load', function () { waitForSwiper(initAll); });
})();